import React, { useEffect } from "react";
import { Card, CardContent, Typography, Grid, Box } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { keyframes } from "@emotion/react";
import {
  FiEdit3,
  FiList,
  FiPlusCircle,
  FiFileText,
  FiCalendar,
  FiArrowRight,
} from "react-icons/fi";

export default function CardGridDashboard() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isAuthenticated = () => {
    const token = localStorage.getItem("token");
    return !!token;
  };

  const getUserRole = () => {
    return localStorage.getItem("role");
  };

  const handleButtonClick = (path) => {
    if (isAuthenticated()) {
      navigate(path);
    } else {
      alert("You must be logged in to access this page.");
    }
  };

  const cardData = [
    {
      title: "Update Ticket",
      description: "Update your daily work.",
      path: "/TicketForm",
      roles: ["Hr", "ProjectManager", "AdminController"],
      icon: <FiEdit3 size={28} />,
      accent: "#0ea5e9",
    },
    {
      title: "View Ticket",
      description: "View employee ticket data.",
      path: "/EmployeeList",
      roles: ["Hr", "ProjectManager", "AdminController"],
      icon: <FiList size={28} />,
      accent: "#8b5cf6",
    },
    {
      title: "SOP-Data-post",
      description: "View SOP data post.",
      path: "/SOP-POST",
      roles: ["Hr", "ProjectManager", "AdminController", "Distributor"],
      icon: <FiPlusCircle size={28} />,
      accent: "#06b6d4",
    },
    {
      title: "SOP-list",
      description: "View SOP data list.",
      path: "/SOP",
      roles: ["Hr", "ProjectManager", "AdminController", "Distributor"],
      icon: <FiFileText size={28} />,
      accent: "#0d9488",
    },
    {
      title: "Employee List Month",
      description: "View employee list data.",
      path: "/employee-data/all",
      roles: ["Hr"],
      icon: <FiCalendar size={28} />,
      accent: "#6366f1",
    },
    {
      title: "Employee Date Range",
      description: "View employee data with date filtering.",
      path: "/EmployeeDateRange",
      roles: ["Hr"],
      icon: <FiCalendar size={28} />,
      accent: "#14b8a6",
    },
  ];

  const userRole = getUserRole();
  const filteredCards = cardData.filter((card) =>
    card.roles.includes(userRole)
  );

  const fadeInUp = keyframes`
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `;

  return (
    <Box style={styles.pageWrap}>
      <Box style={styles.headerSection}>
        <Box style={styles.headerContent}>
          <Typography component="h1" style={styles.pageTitle}>
            Employee Data
          </Typography>
          <Typography style={styles.pageSubtitle}>
            Manage tickets, SOP data, and employee information
          </Typography>
        </Box>
      </Box>

      <Box style={styles.mainContent}>
        <Grid
          container
          spacing={3}
          style={styles.cardContainer}
          sx={{
            animation: `${fadeInUp} 0.5s ease-out forwards`,
          }}
        >
          {filteredCards.map((card, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={index}
              sx={{
                animation: `${fadeInUp} 0.4s ease-out ${index * 0.05}s forwards`,
                opacity: 0,
              }}
            >
              <Link
                to={isAuthenticated() ? card.path : "#"}
                style={styles.link}
              >
                <Card
                  style={styles.card}
                  onClick={() => handleButtonClick(card.path)}
                  sx={{
                    cursor: "pointer",
                    position: "relative",
                    "&:before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(to right, #0048e6, #9C15F7, rgba(255, 75, 248, 0.3))",
                      opacity: 0,
                      zIndex: 0,
                      transition: "opacity 0.25s ease",
                    },
                    "&:hover": {
                      "&:before": {
                        opacity: 0.28,
                      },
                    },
                  }}
                >
                  <CardContent style={styles.cardContent}>
                    <Box
                      style={{
                        ...styles.iconWrap,
                        color: card.accent,
                        backgroundColor: `${card.accent}14`,
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Typography component="h3" style={styles.cardTitle}>
                      {card.title}
                    </Typography>
                    <Typography style={styles.cardDescription}>
                      {card.description}
                    </Typography>
                    <Box style={styles.cardArrow}>
                      <span style={styles.arrowText}>Open</span>
                      <FiArrowRight size={16} style={{ flexShrink: 0 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Link>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}

const styles = {
  pageWrap: {
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
  },
  headerSection: {
    padding: "2rem 1.5rem 1.5rem",
    background:
      "linear-gradient(180deg, rgba(15, 23, 42, 0.03) 0%, transparent 100%)",
    borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
  },
  headerContent: {
    maxWidth: 1200,
    margin: "0 auto",
  },
  pageTitle: {
    fontSize: "clamp(1.75rem, 4vw, 2.25rem)",
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.02em",
    margin: 0,
    lineHeight: 1.2,
  },
  pageSubtitle: {
    fontSize: "clamp(0.9rem, 2vw, 1rem)",
    color: "#64748b",
    marginTop: "0.35rem",
    fontWeight: 400,
  },
  mainContent: {
    maxWidth: 1280,
    margin: "0 auto",
    padding: "1.5rem",
    paddingBottom: "3rem",
  },
  cardContainer: {
    justifyContent: "flex-start",
  },
  link: {
    textDecoration: "none",
    color: "inherit",
    display: "block",
    height: "100%",
  },
  card: {
    height: "100%",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    border: "1px solid rgba(15, 23, 42, 0.08)",
    backgroundColor: "#ffffff",
  },
  cardContent: {
    padding: "1.5rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    height: "100%",
    boxSizing: "border-box",
    position: "relative",
    zIndex: 1,
  },
  iconWrap: {
    width: 52,
    height: 52,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "1rem",
  },
  cardTitle: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#0f172a",
    margin: 0,
    marginBottom: "0.35rem",
    lineHeight: 1.3,
    letterSpacing: "-0.01em",
  },
  cardDescription: {
    fontSize: "0.875rem",
    color: "#64748b",
    lineHeight: 1.45,
    marginBottom: "1rem",
    flex: 1,
  },
  cardArrow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "#475569",
    opacity: 0.85,
  },
  arrowText: {
    margin: 0,
  },
};
